import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import * as Phosphor from "@phosphor-icons/react";
import { fetchAllSnapshots, fetchRecommendations } from "@/lib/api";
import { regionHealth, healthBand, toneColor } from "@/lib/laymen";
import { StoryCard, ExplainPanel } from "@/components/Explain";
import GlossaryTerm from "@/components/GlossaryTerm";

/**
 * Simplified "traffic-light" view for farmers on-the-go.
 * One big circle per region with a dominant color and the ONE most important action.
 * Designed for mobile (touch-first), huge text, zero jargon.
 */
export default function QuickStatus() {
  const [snaps, setSnaps] = useState([]);
  const [regions, setRegions] = useState([]);
  const [recsByRegion, setRecsByRegion] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const all = await fetchAllSnapshots();
        setSnaps(all.snapshots || []);
        setRegions(all.regions || []);
        const recs = await Promise.all(
          (all.regions || []).map(async (r) => {
            try {
              const d = await fetchRecommendations(r.id);
              return [r.id, d.recommendations || []];
            } catch {
              return [r.id, []];
            }
          })
        );
        setRecsByRegion(Object.fromEntries(recs));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] relative z-10" data-testid="quick-root">
      <Helmet>
        <title>Tages-Ampel · Bodenblick</title>
        <meta name="description" content="Schneller Ampel-Überblick pro Region: rot, gelb oder grün. Für Bauern und Förster unterwegs." />
      </Helmet>

      <header className="border-b border-white/10 bg-[#121212] sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-sm text-zinc-400">
            <Phosphor.ArrowLeft size={16} /> Cockpit
          </Link>
          <div className="text-right">
            <div className="eid-label">Tages-Ampel</div>
            <div className="text-sm font-medium">Bodenblick</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        <StoryCard
          eyebrow="So funktioniert die Tages-Ampel"
          title="In drei Sekunden: Wie geht es heute deiner Region?"
          lead="Bodenblick verdichtet Satelliten- und Wetter-Daten zu einer einzigen Zahl von 0 bis 100 — und einer Farbe. Rot heißt: bitte hinschauen. Gelb heißt: aufmerksam bleiben. Grün heißt: heute ist alles im Lot."
          bullets={[
            "Score < 40 → rot (Stress sichtbar — Feldgang empfohlen)",
            "Score 40–69 → gelb (auffällig, aber noch im Rahmen)",
            "Score ≥ 70 → grün (Wachstum verläuft normal)",
          ]}
          accent="#FF5500"
          icon={Phosphor.TrafficSign}
          testid="quick-story"
        />
        <ExplainPanel
          title="Was steckt hinter der Zahl?"
          accent="#FF5500"
          testid="quick-explain"
        >
          <p>
            Die Tages-Ampel kombiniert drei Live-Quellen zu einem einzigen Gesundheits-Score:
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><GlossaryTerm word="NDVI">NDVI</GlossaryTerm> aus <GlossaryTerm word="Sentinel-2">Sentinel-2</GlossaryTerm>-Satellitenbildern (alle 5 Tage) — zeigt wie viel Chlorophyll deine Felder gerade haben.</li>
            <li><GlossaryTerm word="Bodenfeuchte">Bodenfeuchte</GlossaryTerm> aus <GlossaryTerm word="Open-Meteo">Open-Meteo</GlossaryTerm> (<GlossaryTerm word="ERA5">ERA5</GlossaryTerm>-Modell) — wieviel Wasser im Wurzelbereich verfügbar ist.</li>
            <li>Wetter-Anomalien der letzten 14 Tage — Hitze-Wellen, Frost, ungewöhnliche Trockenheit.</li>
          </ul>
          <p>
            Der untere Teil zeigt die <strong>eine</strong> wichtigste Handlung, die heute den größten Unterschied macht — z. B. „heute spritzen", „Wasser geben", „Bienenflug beobachten". Wenn keine Aktion nötig ist, steht da „Routine-Check genügt" — und das ist eine gute Nachricht.
          </p>
          <p className="text-xs text-zinc-400 mt-2 pt-2 border-t border-white/15">
            Quelle: Copernicus Sentinel-2 (ESA), Open-Meteo Reanalysis (ERA5), Bodenblick Modellierung.
          </p>
        </ExplainPanel>

        {loading && (
          <div className="eid-card p-8 text-center text-sm text-zinc-400" data-testid="quick-loading">
            Lade Daten…
          </div>
        )}

        {!loading && regions.map((r) => {
          const snap = snaps.find((s) => s.region_id === r.id);
          const score = regionHealth(snap?.metrics || {});
          const band = healthBand(score);
          const c = toneColor(band.level);
          const topRec = (recsByRegion[r.id] || [])[0];

          return (
            <section
              key={r.id}
              className="eid-card overflow-hidden"
              data-testid={`quick-card-${r.id}`}
              style={{ borderLeft: `4px solid ${c.dot}` }}
            >
              <div
                className="p-6 flex flex-col items-center text-center"
                style={{ background: `linear-gradient(180deg, ${c.bg} 0%, white 70%)` }}
              >
                <div className="eid-label">{r.public_label}</div>
                <h2 className="text-2xl font-medium tracking-tight mt-1">{r.label}</h2>
                <div
                  className="mt-5 w-40 h-40 rounded-full flex items-center justify-center text-4xl font-mono relative"
                  style={{
                    background: c.dot,
                    color: "white",
                    boxShadow: `0 0 0 8px ${c.bg}`,
                  }}
                  data-testid={`quick-light-${r.id}`}
                >
                  <div className="flex flex-col items-center leading-none">
                    <span className="text-5xl font-medium">{score}</span>
                    <span className="text-xs font-normal tracking-widest uppercase mt-1 opacity-90">
                      {band.label}
                    </span>
                  </div>
                </div>

                <p className="text-sm text-zinc-400 mt-4 max-w-sm">
                  {r.description}
                </p>
              </div>

              {topRec ? (
                <div className="px-6 pb-6" data-testid={`quick-action-${r.id}`}>
                  <div className="eid-label">Wichtigste Handlung heute</div>
                  <div
                    className="mt-2 p-4 rounded-sm flex items-start gap-3"
                    style={{ background: toneColor(
                      topRec.priority === "high" ? "critical" :
                      topRec.priority === "medium" ? "warn" : "good"
                    ).bg }}
                  >
                    <span
                      className="p-2 rounded-sm mt-0.5"
                      style={{
                        background: "white",
                        color: toneColor(
                          topRec.priority === "high" ? "critical" :
                          topRec.priority === "medium" ? "warn" : "good"
                        ).fg,
                      }}
                    >
                      {React.createElement(
                        Phosphor[topRec.icon] || Phosphor.Info,
                        { size: 26, weight: "regular" }
                      )}
                    </span>
                    <div>
                      <div className="font-medium text-lg">{topRec.title}</div>
                      <div className="text-sm text-zinc-400 mt-1">{topRec.reason}</div>
                    </div>
                  </div>

                  <Link
                    to="/dashboard"
                    className="mt-3 block text-center text-sm underline text-[#00FA9A]"
                  >
                    Mehr Details im Cockpit →
                  </Link>
                </div>
              ) : (
                <div className="px-6 pb-6 text-center text-sm text-zinc-500" data-testid={`quick-action-empty-${r.id}`}>
                  Routine-Check genügt.
                </div>
              )}
            </section>
          );
        })}

        <div className="text-center pt-4 pb-8">
          <Link
            to="/insights"
            data-testid="quick-to-insights"
            className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white"
          >
            <Phosphor.Newspaper size={14} /> Wöchentlicher Überblick
          </Link>
        </div>
      </main>
    </div>
  );
}
